/**
 * Tombstone rendering utilities
 * Generates appropriate tombstone text based on content type, ownership, and deletion type
 * Per FR-6: Tombstone Copy (Authoritative)
 */

export type ContentType = 'post' | 'reply';
export type DeletedByType = 'user' | 'moderator';

/**
 * Generate tombstone text for deleted content
 * 
 * @param contentType - 'post' for root posts, 'reply' for replies
 * @param isOwner - Whether the current user owns the content
 * @param deletedByType - 'user' if deleted by owner, 'moderator' if deleted by admin
 * @returns Tombstone text string
 */
export function getTombstoneText(
  contentType: ContentType,
  isOwner: boolean,
  deletedByType: DeletedByType
): string {
  const isPost = contentType === 'post';
  const isUserDeleted = deletedByType === 'user';

  // Root post, owner, user-deleted
  if (isPost && isOwner && isUserDeleted) {
    return '[You removed this post]';
  }

  // Root post, owner, moderator-deleted
  if (isPost && isOwner && !isUserDeleted) {
    return '[This post was removed by a moderator]';
  }

  // Root post, others, user-deleted
  if (isPost && !isOwner && isUserDeleted) {
    return '[This post was removed]';
  }

  // Root post, others, moderator-deleted
  if (isPost && !isOwner && !isUserDeleted) {
    return '[This post was removed by a moderator]';
  }

  // Reply, owner, user-deleted
  if (!isPost && isOwner && isUserDeleted) {
    return '[You removed this reply]';
  }

  // Reply, owner, moderator-deleted
  if (!isPost && isOwner && !isUserDeleted) {
    return '[This reply was removed by a moderator]';
  }

  // Reply, others, user-deleted
  if (!isPost && !isOwner && isUserDeleted) {
    return '[This reply was removed]';
  }

  // Reply, others, moderator-deleted
  if (!isPost && !isOwner && !isUserDeleted) {
    return '[This reply was removed by a moderator]';
  }

  // Fallback (should never reach here, but TypeScript requires it)
  return '[This content was removed]';
}

/**
 * Check if a reply has children (nested replies)
 * 
 * @param reply - Reply object with optional replies array
 * @returns true if reply has children, false otherwise
 */
export function hasChildren(reply: { replies?: Array<unknown> }): boolean {
  return reply.replies !== undefined && reply.replies.length > 0;
}

/**
 * Filter out deleted leaf replies from a nested reply tree
 * Only filters user-deleted leaf replies (deleted_by_type='user' or null/undefined)
 * Moderator-deleted leaf replies are kept so they can show tombstones
 * A deleted leaf reply is one with audio_url: null and no children
 * 
 * @param replies - Array of replies to filter
 * @returns Filtered array with user-deleted leaf replies removed (moderator-deleted leaf replies kept)
 */
export function filterDeletedLeafReplies<T extends { audio_url: string | null; replies?: T[]; deleted_by_type?: DeletedByType }>(
  replies: T[]
): T[] {
  return replies
    .map((reply): T | null => {
      // Recursively filter children first
      const filteredChildren = reply.replies 
        ? filterDeletedLeafReplies(reply.replies)
        : undefined;
      
      // Create reply with filtered children
      const replyWithFilteredChildren: T = {
        ...reply,
        replies: filteredChildren,
      } as T;
      
      // Check if this is a deleted leaf reply (audio_url is null AND no children)
      const isDeletedLeaf = reply.audio_url === null && !hasChildren(replyWithFilteredChildren);
      
      // Only filter out user-deleted leaf replies (or null/undefined, which defaults to 'user')
      // Keep moderator-deleted leaf replies so they can show tombstones
      const isUserDeleted = reply.deleted_by_type === 'user' || reply.deleted_by_type === undefined || reply.deleted_by_type === null;
      const shouldFilter = isDeletedLeaf && isUserDeleted;
      
      if (shouldFilter) {
        return null;
      }
      
      return replyWithFilteredChildren;
    })
    .filter((reply): reply is T => reply !== null);
}

